/*
 * SALC Motion
 * Dependency-free carousel controller for Shopify and regular websites.
 *
 * Required markup:
 * - [data-salc-motion] root
 * - [data-salc-motion-viewport] viewport
 * - [data-salc-motion-track] track
 * - [data-salc-motion-item] slide items
 *
 * Optional controls:
 * - [data-salc-motion-prev]
 * - [data-salc-motion-next]
 * - [data-salc-motion-dots]
 * - [data-salc-motion-autoplay-toggle]
 */
(function (globalScope, factory) {
  'use strict';

  var SALCMotion = factory(globalScope);

  if (typeof module === 'object' && module.exports) {
    module.exports = SALCMotion;
  }

  if (!globalScope || !globalScope.document) return;

  if (
    globalScope.SALCMotion &&
    globalScope.SALCMotion.version === SALCMotion.version &&
    typeof globalScope.SALCMotion.init === 'function'
  ) {
    globalScope.SALCMotion.init(globalScope.document);
    globalScope.SALCMotion.installJQueryPlugin();
    return;
  }

  globalScope.SALCMotion = SALCMotion;
  globalScope.initSALCMotion = SALCMotion.init;
  globalScope.destroySALCMotion = SALCMotion.destroyAll;

  SALCMotion.installJQueryPlugin();
  SALCMotion.bindPlatformEvents();
  SALCMotion.autoInit();
})(typeof window !== 'undefined' ? window : globalThis, function (globalScope) {
  'use strict';

  var VERSION = '2.1.0';
  var documentRef = globalScope && globalScope.document ? globalScope.document : null;
  var ROOT_SELECTOR = '[data-salc-motion]';
  var INSTANCE_KEY = 'salcMotion';
  var INSTANCES = [];
  var uid = 0;

  var DEFAULTS = {
    loop: false,
    autoplay: false,
    autoplayDelay: 4000,
    speed: 320,
    draggable: true,
    pageDots: true,
    groupCells: false,
    prevNextButtons: true,
    pauseOnHover: true,
    watchCSS: false,
    centerMode: false,
    centerMobile: false,
    centerBreakpoint: 768,
    initialIndex: 0,
    observeMutations: true,
    direction: 'auto',
    carouselLabel: 'Carousel',
    roleDescription: 'carousel',
    slideRoleDescription: 'slide',
    slideLabel: 'Slide {index} of {count}',
    previousLabel: 'Previous slide',
    nextLabel: 'Next slide',
    dotLabel: 'Go to slide {index} of {count}',
    pauseLabel: 'Pause carousel',
    playLabel: 'Play carousel'
  };

  var OPTION_DEFINITIONS = [
    { key: 'loop', attributes: ['data-salc-motion-loop', 'data-loop'], parser: parseBoolean },
    { key: 'autoplay', attributes: ['data-salc-motion-autoplay', 'data-autoplay'], parser: parseBoolean },
    {
      key: 'autoplayDelay',
      attributes: ['data-salc-motion-autoplay-delay', 'data-autoplay-delay'],
      parser: function (value, fallback) { return parseNumber(value, fallback, 1000, 600000); }
    },
    {
      key: 'speed',
      attributes: ['data-salc-motion-speed', 'data-animation-duration'],
      parser: function (value, fallback) { return parseNumber(value, fallback, 0, 5000); }
    },
    { key: 'draggable', attributes: ['data-salc-motion-draggable', 'data-draggable'], parser: parseBoolean },
    { key: 'pageDots', attributes: ['data-salc-motion-page-dots', 'data-page-dots'], parser: parseBoolean },
    { key: 'groupCells', attributes: ['data-salc-motion-group-cells'], parser: parseBoolean },
    {
      key: 'prevNextButtons',
      attributes: ['data-salc-motion-prev-next-buttons', 'data-prev-next-buttons'],
      parser: parseBoolean
    },
    {
      key: 'pauseOnHover',
      attributes: ['data-salc-motion-pause-on-hover', 'data-pause-on-hover'],
      parser: parseBoolean
    },
    {
      key: 'watchCSS',
      attributes: ['data-salc-motion-watch-css', 'data-watch-css'],
      parser: parseBoolean
    },
    {
      key: 'centerMode',
      attributes: ['data-salc-motion-center-mode', 'data-center-mode'],
      parser: parseBoolean
    },
    {
      key: 'centerMobile',
      attributes: ['data-salc-motion-center-mobile', 'data-center-mobile'],
      parser: parseBoolean
    },
    {
      key: 'centerBreakpoint',
      attributes: ['data-salc-motion-center-breakpoint', 'data-center-breakpoint'],
      parser: function (value, fallback) { return parseNumber(value, fallback, 0, 5000); }
    },
    {
      key: 'initialIndex',
      attributes: ['data-salc-motion-initial-index', 'data-initial-index'],
      parser: function (value, fallback) { return parseNumber(value, fallback, 0, 100000); }
    },
    {
      key: 'observeMutations',
      attributes: ['data-salc-motion-observe-mutations', 'data-observe-mutations'],
      parser: parseBoolean
    },
    { key: 'direction', attributes: ['data-salc-motion-direction'], parser: parseDirection },
    { key: 'carouselLabel', attributes: ['data-salc-motion-label'], parser: parseString },
    {
      key: 'roleDescription',
      attributes: ['data-salc-motion-role-description'],
      parser: parseString
    },
    {
      key: 'slideRoleDescription',
      attributes: ['data-salc-motion-slide-role-description'],
      parser: parseString
    },
    { key: 'slideLabel', attributes: ['data-salc-motion-slide-label'], parser: parseString },
    { key: 'previousLabel', attributes: ['data-salc-motion-previous-label'], parser: parseString },
    { key: 'nextLabel', attributes: ['data-salc-motion-next-label'], parser: parseString },
    { key: 'dotLabel', attributes: ['data-salc-motion-dot-label'], parser: parseString },
    { key: 'pauseLabel', attributes: ['data-salc-motion-pause-label'], parser: parseString },
    { key: 'playLabel', attributes: ['data-salc-motion-play-label'], parser: parseString }
  ];

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable="true"]',
    '[tabindex]'
  ].join(',');

  var UNSAFE_CLONE_SELECTOR = [
    'form',
    'iframe',
    'object',
    'embed',
    'audio',
    'video',
    'canvas',
    '[contenteditable]',
    '[data-salc-motion-no-clone]'
  ].join(',');
  var ID_REFERENCE_ATTRIBUTES = [
    'for',
    'headers',
    'list',
    'aria-activedescendant',
    'aria-controls',
    'aria-describedby',
    'aria-details',
    'aria-errormessage',
    'aria-flowto',
    'aria-labelledby',
    'aria-owns'
  ];

  function parseBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    if (value === true || value === 1) return true;
    if (value === false || value === 0) return false;

    var normalized = String(value).toLowerCase().trim();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') return false;
    return fallback;
  }

  function parseNumber(value, fallback, minimum, maximum) {
    if (value === undefined || value === null || value === '') return fallback;

    var number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return clamp(number, minimum, maximum);
  }

  function parseDirection(value, fallback) {
    var normalized = String(value || '').toLowerCase().trim();
    return normalized === 'ltr' || normalized === 'rtl' || normalized === 'auto' ? normalized : fallback;
  }

  function parseString(value, fallback) {
    if (value === undefined || value === null || String(value).trim() === '') return fallback;
    return String(value);
  }

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(value, maximum));
  }

  function normalizeIndex(index, count) {
    return count > 0 ? ((index % count) + count) % count : 0;
  }

  function replaceTokens(template, values) {
    return String(template).replace(/\{([a-z]+)\}/gi, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function isElement(value) {
    return Boolean(value && value.nodeType === 1);
  }

  function resolveElement(value) {
    if (isElement(value)) return value;
    if (typeof value === 'string' && documentRef) return documentRef.querySelector(value);
    return null;
  }

  function readAttribute(root, names) {
    for (var index = 0; index < names.length; index += 1) {
      if (root.hasAttribute(names[index])) return root.getAttribute(names[index]);
    }
    return undefined;
  }

  function readOptions(root, suppliedOptions) {
    var options = {};
    var supplied = suppliedOptions || {};

    OPTION_DEFINITIONS.forEach(function (definition) {
      var fallback = DEFAULTS[definition.key];
      var attributeValue = readAttribute(root, definition.attributes);
      var value = definition.parser(attributeValue, fallback);

      if (Object.prototype.hasOwnProperty.call(supplied, definition.key)) {
        value = definition.parser(supplied[definition.key], value);
      }

      options[definition.key] = value;
    });

    return options;
  }

  function debounce(callback, delay) {
    var timeout = null;

    function debounced() {
      var args = arguments;
      globalScope.clearTimeout(timeout);
      timeout = globalScope.setTimeout(function () {
        timeout = null;
        callback.apply(null, args);
      }, delay);
    }

    debounced.cancel = function () {
      globalScope.clearTimeout(timeout);
      timeout = null;
    };

    return debounced;
  }

  function getWatchCSSValue(root) {
    if (!globalScope.getComputedStyle) return '';
    var content = globalScope.getComputedStyle(root, '::after').content;
    return content ? content.replace(/["']/g, '').trim() : '';
  }

  function releasePointerCaptureSafely(target, pointerId) {
    if (!target || pointerId === null || pointerId === undefined || !target.releasePointerCapture) return;

    try {
      if (!target.hasPointerCapture || target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
    } catch (error) {
      return;
    }
  }

  function setPointerCaptureSafely(target, pointerId) {
    if (!target || pointerId === null || pointerId === undefined || !target.setPointerCapture) return;

    try {
      target.setPointerCapture(pointerId);
    } catch (error) {
      return;
    }
  }

  function dispatch(target, eventName, detail) {
    if (!target || !target.dispatchEvent || !documentRef) return;

    var event;
    if (typeof globalScope.CustomEvent === 'function') {
      event = new globalScope.CustomEvent(eventName, { bubbles: true, composed: true, detail: detail });
    } else {
      event = documentRef.createEvent('CustomEvent');
      event.initCustomEvent(eventName, true, false, detail);
    }

    target.dispatchEvent(event);
  }

  function captureAttributes(element, names) {
    var values = {};
    if (!element) return values;

    names.forEach(function (name) {
      values[name] = element.hasAttribute(name) ? element.getAttribute(name) : null;
    });
    return values;
  }

  function restoreAttributes(element, values) {
    if (!element) return;

    Object.keys(values).forEach(function (name) {
      if (values[name] === null) element.removeAttribute(name);
      else element.setAttribute(name, values[name]);
    });
  }

  function unregister(instance) {
    var index = INSTANCES.indexOf(instance);
    if (index >= 0) INSTANCES.splice(index, 1);
  }

  function getFocusableElements(root) {
    var elements = root.matches && root.matches(FOCUSABLE_SELECTOR) ? [root] : [];
    return elements.concat(toArray(root.querySelectorAll(FOCUSABLE_SELECTOR)));
  }

  function SALCMotion(root, options) {
    var resolvedRoot = resolveElement(root);
    if (!resolvedRoot) throw new TypeError('SALC Motion requires a valid root element or selector.');
    if (resolvedRoot[INSTANCE_KEY]) return resolvedRoot[INSTANCE_KEY];

    this.id = 'salc-motion-' + (++uid);
    this.root = resolvedRoot;
    this.viewport = resolvedRoot.querySelector('[data-salc-motion-viewport]');
    this.track = resolvedRoot.querySelector('[data-salc-motion-track]');
    this.prevButton = resolvedRoot.querySelector('[data-salc-motion-prev]');
    this.nextButton = resolvedRoot.querySelector('[data-salc-motion-next]');
    this.dotsContainer = resolvedRoot.querySelector('[data-salc-motion-dots]');
    this.autoplayToggle = resolvedRoot.querySelector('[data-salc-motion-autoplay-toggle]');
    this.suppliedOptions = options || {};
    this.options = readOptions(resolvedRoot, this.suppliedOptions);
    this.realItems = [];
    this.items = [];
    this.positions = [];
    this.widths = [];
    this.slideStops = [];
    this.dots = [];
    this.index = this.options.initialIndex;
    this.positionIndex = 0;
    this.cloneCount = 0;
    this.effectiveLoop = false;
    this.isRTL = false;
    this.isEnabled = false;
    this.isManuallyDisabled = false;
    this.isDestroyed = false;
    this.isTransitioning = false;
    this.pendingDelta = 0;
    this.pendingTarget = null;
    this.timer = null;
    this.transitionTimer = null;
    this.pointerId = null;
    this.startX = 0;
    this.startY = 0;
    this.dragDelta = 0;
    this.dragMoved = false;
    this.dragAxisLocked = false;
    this.isPointerDown = false;
    this.suppressClickUntil = 0;
    this.hoverPaused = false;
    this.dragPaused = false;
    this.reducedMotionQuery = globalScope.matchMedia
      ? globalScope.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    this.hasExplicitAutoplayChoice = false;
    this.userPaused = Boolean(this.options.autoplay && this.reducedMotionQuery && this.reducedMotionQuery.matches);
    this.generatedAutoplayToggle = false;
    this.unbinders = [];
    this.resizeObserver = null;
    this.observedResizeTargets = [];
    this.mutationObserver = null;
    this.itemStates = [];
    this.originalRootAttributes = captureAttributes(this.root, ['role', 'aria-label', 'aria-roledescription']);
    this.originalTrackAttributes = captureAttributes(this.track, ['aria-live']);
    this.originalPrevButtonAttributes = captureAttributes(this.prevButton, ['aria-label', 'hidden', 'disabled']);
    this.originalNextButtonAttributes = captureAttributes(this.nextButton, ['aria-label', 'hidden', 'disabled']);
    this.originalDotsAttributes = captureAttributes(this.dotsContainer, ['hidden']);
    this.originalAutoplayToggleAttributes = captureAttributes(this.autoplayToggle, ['aria-label', 'hidden']);
    this.originalTrackStyle = this.track ? {
      transform: this.track.style.transform,
      transitionDuration: this.track.style.transitionDuration,
      paddingLeft: this.track.style.paddingLeft,
      paddingRight: this.track.style.paddingRight,
      direction: this.track.style.direction
    } : null;
    this.scheduleRefresh = debounce(this.refresh.bind(this), 120);

    resolvedRoot[INSTANCE_KEY] = this;
    INSTANCES.push(this);
    this.init();
  }

  SALCMotion.prototype.init = function () {
    if (!this.viewport || !this.track) {
      this.isDestroyed = true;
      this.root[INSTANCE_KEY] = null;
      unregister(this);
      dispatch(this.root, 'salc-motion:error', {
        instance: this,
        message: 'A viewport and track are required.'
      });
      return;
    }

    this.applySemantics();
    this.bindEvents();
    this.bindResize();
    this.bindMutations();
    this.refresh(false);

    dispatch(this.root, 'salc-motion:init', { instance: this });
  };

  SALCMotion.prototype.applySemantics = function () {
    if (this.originalRootAttributes.role === null) this.root.setAttribute('role', 'region');
    if (this.originalRootAttributes['aria-roledescription'] === null) {
      this.root.setAttribute('aria-roledescription', this.options.roleDescription);
    }
    if (this.originalRootAttributes['aria-label'] === null && !this.root.hasAttribute('aria-labelledby')) {
      this.root.setAttribute('aria-label', this.options.carouselLabel);
    }
    if (!this.track.hasAttribute('aria-live')) this.track.setAttribute('aria-live', 'polite');

    if (this.prevButton && this.originalPrevButtonAttributes['aria-label'] === null) {
      this.prevButton.setAttribute('aria-label', this.options.previousLabel);
    }
    if (this.nextButton && this.originalNextButtonAttributes['aria-label'] === null) {
      this.nextButton.setAttribute('aria-label', this.options.nextLabel);
    }
  };

  SALCMotion.prototype.readOptions = function () {
    var autoplayWasEnabled = this.options.autoplay;
    this.options = readOptions(this.root, this.suppliedOptions);

    if (autoplayWasEnabled !== this.options.autoplay) {
      this.hasExplicitAutoplayChoice = false;
      this.userPaused = Boolean(
        this.options.autoplay && this.reducedMotionQuery && this.reducedMotionQuery.matches
      );
    }

    this.applySemantics();
    return this.options;
  };

  SALCMotion.prototype.addListener = function (target, eventName, callback, options) {
    if (!target || !target.addEventListener) return;

    var listenerOptions = options || false;
    target.addEventListener(eventName, callback, listenerOptions);
    this.unbinders.push(function () {
      target.removeEventListener(eventName, callback, listenerOptions);
    });
  };

  SALCMotion.prototype.bindEvents = function () {
    var self = this;

    this.addListener(this.prevButton, 'click', function () {
      self.pauseByUser();
      self.previous('control');
    });

    this.addListener(this.nextButton, 'click', function () {
      self.pauseByUser();
      self.next('control');
    });

    this.addListener(this.dotsContainer, 'click', function (event) {
      var dot = event.target.closest ? event.target.closest('[data-salc-motion-dot-index]') : null;
      if (!dot || !self.dotsContainer.contains(dot)) return;

      self.pauseByUser();
      self.goTo(Number(dot.getAttribute('data-salc-motion-dot-index')), 'dot');
    });

    this.addListener(this.root, 'mouseenter', function () {
      if (!self.options.pauseOnHover) return;
      self.hoverPaused = true;
      self.stopAutoplay();
    });

    this.addListener(this.root, 'mouseleave', function () {
      if (!self.options.pauseOnHover) return;
      self.hoverPaused = false;
      self.startAutoplay();
    });

    this.addListener(this.root, 'focusin', function (event) {
      if (event.relatedTarget && self.root.contains(event.relatedTarget)) return;
      self.pauseByUser();
    });

    this.addListener(this.viewport, 'pointerdown', function (event) {
      self.onPointerDown(event);
    });
    this.addListener(this.viewport, 'pointermove', function (event) {
      self.onPointerMove(event);
    });
    this.addListener(this.viewport, 'pointerup', function (event) {
      self.onPointerUp(event);
    });
    this.addListener(this.viewport, 'pointercancel', function (event) {
      self.onPointerCancel(event);
    });
    this.addListener(this.viewport, 'lostpointercapture', function (event) {
      self.onPointerCancel(event);
    });
    this.addListener(this.viewport, 'click', function (event) {
      self.onClick(event);
    }, true);

    this.addListener(this.track, 'transitionend', function (event) {
      if (event.target !== self.track || event.propertyName !== 'transform') return;
      self.completeTransition();
    });

    this.addListener(documentRef, 'visibilitychange', function () {
      if (documentRef.hidden) self.stopAutoplay();
      else self.startAutoplay();
    });

    if (this.reducedMotionQuery) {
      var onReducedMotionChange = function (event) {
        if (event.matches && !self.hasExplicitAutoplayChoice) {
          self.userPaused = true;
          self.stopAutoplay();
        }
        self.scheduleRefresh(false);
      };

      if (this.reducedMotionQuery.addEventListener) {
        this.addListener(this.reducedMotionQuery, 'change', onReducedMotionChange);
      } else if (this.reducedMotionQuery.addListener) {
        this.reducedMotionQuery.addListener(onReducedMotionChange);
        this.unbinders.push(function () {
          self.reducedMotionQuery.removeListener(onReducedMotionChange);
        });
      }
    }
  };

  SALCMotion.prototype.bindResize = function () {
    var self = this;
    var onResize = function () {
      if (!self.isDestroyed) self.scheduleRefresh(true);
    };

    this.addListener(globalScope, 'resize', onResize, { passive: true });

    if ('ResizeObserver' in globalScope) {
      this.resizeObserver = new globalScope.ResizeObserver(onResize);
    }
  };

  SALCMotion.prototype.bindMutations = function () {
    if (!('MutationObserver' in globalScope)) return;

    var self = this;
    this.mutationObserver = new globalScope.MutationObserver(function (mutations) {
      var requiresRefresh = mutations.some(function (mutation) {
        if (mutation.type === 'attributes' && mutation.target === self.root) return true;
        if (!self.options.observeMutations) return false;
        if (mutation.type !== 'childList' || mutation.target !== self.track) return false;

        return toArray(mutation.addedNodes).concat(toArray(mutation.removedNodes)).some(function (node) {
          return isElement(node) &&
            node.hasAttribute('data-salc-motion-item') &&
            !node.hasAttribute('data-salc-motion-clone');
        });
      });

      if (requiresRefresh) self.scheduleRefresh(true);
    });

    this.mutationObserver.observe(this.root, {
      attributes: true,
      attributeFilter: OPTION_DEFINITIONS.reduce(function (attributes, definition) {
        return attributes.concat(definition.attributes);
      }, []).concat(['dir']),
      childList: true,
      subtree: true
    });
  };

  SALCMotion.prototype.observeResizeTargets = function () {
    if (!this.resizeObserver) return;

    var targets = [this.root, this.viewport].concat(this.realItems);
    var targetsChanged = targets.length !== this.observedResizeTargets.length || targets.some(function (target, index) {
      return target !== this.observedResizeTargets[index];
    }, this);

    if (!targetsChanged) return;

    this.resizeObserver.disconnect();
    targets.forEach(function (target) { this.resizeObserver.observe(target); }, this);
    this.observedResizeTargets = targets;
  };

  SALCMotion.prototype.shouldEnableByCSS = function () {
    if (!this.options.watchCSS) return true;
    var value = getWatchCSSValue(this.root);
    return value === 'salc-motion' || value === 'flickity';
  };

  SALCMotion.prototype.getRealItems = function () {
    if (!this.track) return [];

    return toArray(this.track.querySelectorAll('[data-salc-motion-item]:not([data-salc-motion-clone])'))
      .filter(function (item) { return item.parentNode === this.track; }, this);
  };

  SALCMotion.prototype.applyItemSemantics = function () {
    this.realItems.forEach(function (item, index) {
      var state = this.rememberItemState(item);

      if (state.attributes.role === null) item.setAttribute('role', 'group');
      if (state.attributes['aria-roledescription'] === null) {
        item.setAttribute('aria-roledescription', this.options.slideRoleDescription);
      }
      if (state.attributes['aria-label'] === null && !item.hasAttribute('aria-labelledby')) {
        item.setAttribute('aria-label', replaceTokens(this.options.slideLabel, {
          index: index + 1,
          count: this.realItems.length
        }));
      }
    }, this);
  };

  SALCMotion.prototype.getTrackItems = function () {
    if (!this.track) return [];
    return toArray(this.track.querySelectorAll('[data-salc-motion-item]'))
      .filter(function (item) { return item.parentNode === this.track; }, this);
  };

  SALCMotion.prototype.refresh = function (readConfiguration) {
    if (this.isDestroyed) return this;

    var selectedItem = this.getSelectedRealItem();
    var previousIndex = this.index;

    if (readConfiguration !== false) this.readOptions();

    this.stopAutoplay();
    this.cancelTransition();
    this.removeClones();
    this.restoreAllItemStates();
    this.realItems = this.getRealItems();
    this.applyItemSemantics();
    this.observeResizeTargets();
    this.updateDirection();

    if (!this.realItems.length || this.isManuallyDisabled || !this.shouldEnableByCSS()) {
      this.applyDisabledState();
      dispatch(this.root, 'salc-motion:refresh', { instance: this, enabled: false });
      return this;
    }

    this.isEnabled = true;
    this.root.classList.add('is-salc-motion-ready');
    this.root.classList.remove('is-salc-motion-disabled');
    this.applyCenterPadding();
    this.setupClones();
    this.items = this.getTrackItems();
    this.measureItems();
    this.slideStops = this.effectiveLoop ? [] : this.buildSlideStops();

    if (selectedItem && this.realItems.indexOf(selectedItem) >= 0) {
      this.index = this.effectiveLoop
        ? this.realItems.indexOf(selectedItem)
        : this.findStopForItem(this.realItems.indexOf(selectedItem));
    } else if (this.effectiveLoop) {
      this.index = normalizeIndex(previousIndex, this.realItems.length);
    } else {
      this.index = clamp(previousIndex, 0, Math.max(0, this.slideStops.length - 1));
    }

    this.positionIndex = this.effectiveLoop
      ? this.index + this.cloneCount
      : this.getStopItemIndex(this.index);

    this.ensureAutoplayToggle();
    this.buildDots();
    this.moveToPosition(this.positionIndex, false);
    this.updateVisualState();
    this.startAutoplay();

    dispatch(this.root, 'salc-motion:refresh', { instance: this, enabled: true });
    return this;
  };

  SALCMotion.prototype.updateDirection = function () {
    var direction = this.options.direction;
    if (direction === 'auto') {
      direction = globalScope.getComputedStyle ? globalScope.getComputedStyle(this.root).direction : 'ltr';
    }

    this.isRTL = direction === 'rtl';
    this.track.style.direction = this.isRTL ? 'rtl' : 'ltr';
    this.root.classList.toggle('is-salc-motion-rtl', this.isRTL);
  };

  SALCMotion.prototype.getSelectedRealItem = function () {
    if (!this.realItems.length) return null;
    var realIndex = this.effectiveLoop ? this.index : this.getStopItemIndex(this.index);
    return this.realItems[realIndex] || null;
  };

  SALCMotion.prototype.findStopForItem = function (itemIndex) {
    if (!this.slideStops.length) return 0;

    var bestStop = 0;
    this.slideStops.forEach(function (stop, stopIndex) {
      if (stop.itemIndex <= itemIndex) bestStop = stopIndex;
    });
    return bestStop;
  };

  SALCMotion.prototype.canCloneItems = function () {
    return !this.realItems.some(function (item) {
      var elements = [item].concat(toArray(item.querySelectorAll('*')));
      var hasCustomElement = elements.some(function (element) {
        return element.localName && element.localName.indexOf('-') >= 0;
      });

      return hasCustomElement ||
        item.matches(UNSAFE_CLONE_SELECTOR) ||
        Boolean(item.querySelector(UNSAFE_CLONE_SELECTOR));
    });
  };

  SALCMotion.prototype.setupClones = function () {
    this.cloneCount = 0;
    this.effectiveLoop = false;
    this.root.removeAttribute('data-salc-motion-loop-fallback');

    // Page mode wraps between measured pages without cloning individual items.
    if (this.options.groupCells || !this.options.loop || this.realItems.length < 2 || !this.hasOverflow()) return;

    if (!this.canCloneItems()) {
      this.root.setAttribute('data-salc-motion-loop-fallback', 'unsafe-content');
      dispatch(this.root, 'salc-motion:warning', {
        instance: this,
        code: 'unsafe-loop-content',
        message: 'Loop was disabled because one or more slides contain content that should not be cloned.'
      });
      return;
    }

    this.effectiveLoop = true;
    this.cloneCount = Math.min(this.realItems.length, this.getFullyVisibleItemCount() + 1);

    var firstClones = this.realItems.slice(0, this.cloneCount).map(function (item, index) {
      return this.createClone(item, index);
    }, this);
    var lastStartIndex = this.realItems.length - this.cloneCount;
    var lastClones = this.realItems.slice(lastStartIndex).map(function (item, index) {
      return this.createClone(item, lastStartIndex + index);
    }, this);

    for (var lastIndex = lastClones.length - 1; lastIndex >= 0; lastIndex -= 1) {
      this.track.insertBefore(lastClones[lastIndex], this.track.firstChild);
    }

    firstClones.forEach(function (clone) {
      this.track.appendChild(clone);
    }, this);
  };

  SALCMotion.prototype.createClone = function (item, realIndex) {
    var clone = item.cloneNode(true);
    var cloneElements = [clone].concat(toArray(clone.querySelectorAll('*')));

    clone.setAttribute('data-salc-motion-clone', 'true');
    clone.setAttribute('data-salc-motion-clone-of', String(realIndex));
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    cloneElements.forEach(function (element) {
      if (element.hasAttribute('id')) element.removeAttribute('id');
      ID_REFERENCE_ATTRIBUTES.forEach(function (attribute) {
        if (element.hasAttribute(attribute)) element.removeAttribute(attribute);
      });
    });

    toArray(clone.querySelectorAll(FOCUSABLE_SELECTOR)).forEach(function (element) {
      element.setAttribute('tabindex', '-1');
    });
    if (clone.matches(FOCUSABLE_SELECTOR)) clone.setAttribute('tabindex', '-1');

    toArray(clone.querySelectorAll('button, input, select, textarea, fieldset, optgroup')).forEach(function (element) {
      element.disabled = true;
    });
    if (clone.matches('button, input, select, textarea, fieldset, optgroup')) clone.disabled = true;

    return clone;
  };

  SALCMotion.prototype.removeClones = function () {
    if (!this.track) return;

    toArray(this.track.querySelectorAll('[data-salc-motion-clone]')).forEach(function (clone) {
      if (clone.parentNode) clone.parentNode.removeChild(clone);
    });
    this.cloneCount = 0;
    this.effectiveLoop = false;
  };

  SALCMotion.prototype.hasOverflow = function () {
    if (!this.viewport || !this.track) return false;
    return this.getContentExtent(this.realItems) - this.viewport.getBoundingClientRect().width > 1;
  };

  SALCMotion.prototype.getContentExtent = function (items) {
    if (!items || !items.length) return 0;

    var maximum = 0;
    items.forEach(function (item) {
      var width = item.getBoundingClientRect().width;
      maximum = Math.max(maximum, this.getRawItemPosition(item, width) + width);
    }, this);

    var styles = globalScope.getComputedStyle ? globalScope.getComputedStyle(this.track) : null;
    var endPadding = styles
      ? parseFloat(this.isRTL ? styles.paddingLeft : styles.paddingRight) || 0
      : 0;

    return maximum + endPadding;
  };

  SALCMotion.prototype.getFullyVisibleItemCount = function () {
    if (!this.viewport || !this.realItems.length) return 1;

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var count = 0;

    this.realItems.forEach(function (item) {
      var width = item.getBoundingClientRect().width;
      var position = this.getRawItemPosition(item, width);
      if (width > 0 && position + width <= viewportWidth + 1) count += 1;
    }, this);

    return Math.max(1, count);
  };

  SALCMotion.prototype.applyCenterPadding = function () {
    if (!this.track) return;

    if (!this.shouldCenterActiveSlide() || !this.realItems.length) {
      this.track.style.paddingLeft = '';
      this.track.style.paddingRight = '';
      return;
    }

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var firstItemWidth = this.realItems[0].getBoundingClientRect().width;
    var lastItemWidth = this.realItems[this.realItems.length - 1].getBoundingClientRect().width;
    var startPadding = Math.max(0, (viewportWidth - firstItemWidth) / 2);
    var endPadding = Math.max(0, (viewportWidth - lastItemWidth) / 2);

    this.track.style.paddingLeft = (this.isRTL ? endPadding : startPadding) + 'px';
    this.track.style.paddingRight = (this.isRTL ? startPadding : endPadding) + 'px';
  };

  SALCMotion.prototype.shouldCenterActiveSlide = function () {
    if (this.options.centerMode) return true;
    if (!this.options.centerMobile || !globalScope.matchMedia) return false;
    return globalScope.matchMedia('(max-width: ' + this.options.centerBreakpoint + 'px)').matches;
  };

  SALCMotion.prototype.getRawItemPosition = function (item, suppliedWidth) {
    var width = suppliedWidth === undefined ? item.getBoundingClientRect().width : suppliedWidth;
    if (!this.isRTL) return item.offsetLeft;
    return Math.max(0, this.track.clientWidth - item.offsetLeft - width);
  };

  SALCMotion.prototype.measureItems = function () {
    this.positions = [];
    this.widths = [];

    this.items.forEach(function (item) {
      var width = item.getBoundingClientRect().width;
      this.widths.push(width);
      this.positions.push(this.getRawItemPosition(item, width));
    }, this);
  };

  SALCMotion.prototype.getTargetPosition = function (positionIndex) {
    var position = this.positions[positionIndex];
    if (!Number.isFinite(position)) position = 0;

    if (this.shouldCenterActiveSlide()) {
      var itemWidth = this.widths[positionIndex] || 0;
      var viewportWidth = this.viewport.getBoundingClientRect().width;
      position -= Math.max(0, (viewportWidth - itemWidth) / 2);
    }

    if (!this.effectiveLoop) position = Math.min(position, this.getMaxTranslate());
    return Math.max(0, position);
  };

  SALCMotion.prototype.getMaxTranslate = function () {
    if (!this.viewport || !this.track) return 0;

    var extent = 0;
    this.items.forEach(function (item, index) {
      extent = Math.max(extent, (this.positions[index] || 0) + (this.widths[index] || 0));
    }, this);

    var styles = globalScope.getComputedStyle ? globalScope.getComputedStyle(this.track) : null;
    if (styles) extent += parseFloat(this.isRTL ? styles.paddingLeft : styles.paddingRight) || 0;

    return Math.max(0, extent - this.viewport.getBoundingClientRect().width);
  };

  SALCMotion.prototype.buildSlideStops = function () {
    if (!this.realItems.length) return [{ itemIndex: 0, position: 0 }];

    var stops = [];
    var step = this.options.groupCells ? this.getFullyVisibleItemCount() : 1;
    this.realItems.forEach(function (item, itemIndex) {
      if (itemIndex % step !== 0) return;
      var trackIndex = this.items.indexOf(item);
      var position = this.getTargetPosition(trackIndex);
      var lastStop = stops[stops.length - 1];

      if (!lastStop || Math.abs(position - lastStop.position) > 1) {
        stops.push({ itemIndex: itemIndex, position: position });
      }
    }, this);

    return stops.length ? stops : [{ itemIndex: 0, position: 0 }];
  };

  SALCMotion.prototype.getStopItemIndex = function (stopIndex) {
    if (!this.slideStops.length) return 0;
    var stop = this.slideStops[clamp(stopIndex, 0, this.slideStops.length - 1)];
    return stop ? stop.itemIndex : 0;
  };

  SALCMotion.prototype.getStopPosition = function (stopIndex) {
    if (!this.slideStops.length) return 0;
    var stop = this.slideStops[clamp(stopIndex, 0, this.slideStops.length - 1)];
    return stop ? stop.position : 0;
  };

  SALCMotion.prototype.getPosition = function (positionIndex) {
    return this.effectiveLoop
      ? this.getTargetPosition(positionIndex)
      : this.getStopPosition(this.index);
  };

  SALCMotion.prototype.getTransformX = function (position, dragOffset) {
    var base = this.isRTL ? position : -position;
    return base + (dragOffset || 0);
  };

  SALCMotion.prototype.getEffectiveSpeed = function () {
    if (globalScope.matchMedia && globalScope.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    return this.options.speed;
  };

  SALCMotion.prototype.moveToPosition = function (positionIndex, animate, dragOffset) {
    if (!this.track) return;

    var position = this.getPosition(positionIndex);
    var speed = animate ? this.getEffectiveSpeed() : 0;
    var transformX = this.getTransformX(position, dragOffset);

    this.track.classList.toggle('is-animating', speed > 0);
    this.track.style.transitionDuration = speed + 'ms';
    this.track.style.transform = 'translate3d(' + transformX + 'px, 0, 0)';

    this.isTransitioning = speed > 0;
    globalScope.clearTimeout(this.transitionTimer);

    if (this.isTransitioning) {
      this.transitionTimer = globalScope.setTimeout(this.completeTransition.bind(this), speed + 80);
      this.updateVisibleItems(position, dragOffset || 0);
    } else {
      this.correctLoopPosition();
      position = this.getPosition(this.positionIndex);
      this.updateVisibleItems(position, 0);
    }
  };

  SALCMotion.prototype.cancelTransition = function () {
    globalScope.clearTimeout(this.transitionTimer);
    this.transitionTimer = null;
    this.isTransitioning = false;
    this.pendingDelta = 0;
    this.pendingTarget = null;
    if (this.track) this.track.classList.remove('is-animating');
  };

  SALCMotion.prototype.completeTransition = function () {
    if (!this.isTransitioning) return;

    globalScope.clearTimeout(this.transitionTimer);
    this.transitionTimer = null;
    this.isTransitioning = false;
    this.track.classList.remove('is-animating');
    this.correctLoopPosition();
    this.updateVisualState();

    if (this.pendingTarget !== null) {
      var target = this.pendingTarget;
      this.pendingTarget = null;
      this.pendingDelta = 0;
      this.goTo(target, 'queued');
      return;
    }

    if (this.pendingDelta !== 0) {
      var direction = this.pendingDelta > 0 ? 1 : -1;
      this.pendingDelta -= direction;
      this.navigateBy(direction, 'queued');
    }
  };

  SALCMotion.prototype.correctLoopPosition = function () {
    if (!this.effectiveLoop || !this.realItems.length) return;

    var firstRealPosition = this.cloneCount;
    var afterLastRealPosition = this.cloneCount + this.realItems.length;

    if (this.positionIndex < firstRealPosition || this.positionIndex >= afterLastRealPosition) {
      this.positionIndex = this.index + this.cloneCount;
      var position = this.getTargetPosition(this.positionIndex);
      this.track.style.transitionDuration = '0ms';
      this.track.style.transform = 'translate3d(' + this.getTransformX(position, 0) + 'px, 0, 0)';
      this.updateVisibleItems(position, 0);
    }
  };

  SALCMotion.prototype.previous = function (source) {
    return this.navigateBy(-1, source || 'api');
  };

  SALCMotion.prototype.next = function (source) {
    return this.navigateBy(1, source || 'api');
  };

  SALCMotion.prototype.navigateBy = function (delta, source) {
    if (!this.isEnabled || this.isDestroyed || !this.canMove()) return this;

    var direction = delta < 0 ? -1 : 1;
    if (this.isTransitioning) {
      this.pendingDelta = clamp(this.pendingDelta + direction, -10, 10);
      return this;
    }

    var previousIndex = this.index;
    if (this.effectiveLoop) {
      this.index = normalizeIndex(this.index + direction, this.realItems.length);
      this.positionIndex += direction;
    } else {
      this.index = this.isPageLoop()
        ? normalizeIndex(this.index + direction, this.slideStops.length)
        : clamp(this.index + direction, 0, this.getMaxIndex());
      if (this.index === previousIndex) {
        this.moveToPosition(this.positionIndex, true);
        return this;
      }
      this.positionIndex = this.getStopItemIndex(this.index);
    }

    this.moveToPosition(this.positionIndex, true);
    this.updateVisualState();
    this.emitChange(source);
    return this;
  };

  SALCMotion.prototype.goTo = function (nextIndex, source) {
    if (!this.isEnabled || this.isDestroyed || !this.realItems.length) return this;

    var parsedIndex = Number(nextIndex);
    if (!Number.isFinite(parsedIndex)) return this;

    var targetIndex = this.effectiveLoop
      ? normalizeIndex(Math.round(parsedIndex), this.realItems.length)
      : clamp(Math.round(parsedIndex), 0, this.getMaxIndex());

    if (this.isTransitioning) {
      this.pendingTarget = targetIndex;
      this.pendingDelta = 0;
      return this;
    }

    if (targetIndex === this.index) return this;

    this.index = targetIndex;
    this.positionIndex = this.effectiveLoop
      ? this.index + this.cloneCount
      : this.getStopItemIndex(this.index);

    this.moveToPosition(this.positionIndex, true);
    this.updateVisualState();
    this.emitChange(source || 'api');
    return this;
  };

  SALCMotion.prototype.emitChange = function (source) {
    dispatch(this.root, 'salc-motion:change', {
      instance: this,
      index: this.index,
      itemIndex: this.effectiveLoop ? this.index : this.getStopItemIndex(this.index),
      source: source || 'api'
    });
  };

  SALCMotion.prototype.getMaxIndex = function () {
    return this.effectiveLoop
      ? Math.max(0, this.realItems.length - 1)
      : Math.max(0, this.slideStops.length - 1);
  };

  SALCMotion.prototype.isPageLoop = function () {
    return Boolean(this.options.groupCells && this.options.loop && this.slideStops.length > 1);
  };

  SALCMotion.prototype.canMove = function () {
    return this.effectiveLoop ? this.realItems.length > 1 : this.slideStops.length > 1;
  };

  SALCMotion.prototype.buildDots = function () {
    var dotCount = this.effectiveLoop ? this.realItems.length : this.slideStops.length;

    if (!this.dotsContainer || !this.options.pageDots || dotCount <= 1) {
      if (this.dotsContainer) this.dotsContainer.hidden = true;
      this.dots = [];
      return;
    }

    this.dotsContainer.hidden = false;

    if (this.dotsContainer.children.length !== dotCount) {
      while (this.dotsContainer.firstChild) this.dotsContainer.removeChild(this.dotsContainer.firstChild);

      for (var index = 0; index < dotCount; index += 1) {
        var dot = documentRef.createElement('button');
        dot.type = 'button';
        dot.className = 'salc-motion__dot';
        dot.setAttribute('data-salc-motion-dot-index', String(index));
        dot.setAttribute('data-salc-motion-generated-dot', 'true');
        this.dotsContainer.appendChild(dot);
      }
    }

    this.dots = toArray(this.dotsContainer.children);
    this.dots.forEach(function (dot, index) {
      dot.setAttribute('aria-label', replaceTokens(this.options.dotLabel, {
        index: index + 1,
        count: dotCount
      }));
    }, this);
  };

  SALCMotion.prototype.ensureAutoplayToggle = function () {
    if (!this.options.autoplay || this.realItems.length <= 1) {
      if (this.autoplayToggle) this.autoplayToggle.hidden = true;
      return;
    }

    if (!this.autoplayToggle) {
      this.autoplayToggle = documentRef.createElement('button');
      this.autoplayToggle.type = 'button';
      this.autoplayToggle.className = 'salc-motion__autoplay-toggle';
      this.autoplayToggle.setAttribute('data-salc-motion-autoplay-toggle', '');
      this.autoplayToggle.setAttribute('data-salc-motion-generated', 'true');
      this.root.appendChild(this.autoplayToggle);
      this.generatedAutoplayToggle = true;

      var self = this;
      this.addListener(this.autoplayToggle, 'click', function () {
        self.toggleAutoplay();
      });
    }

    this.autoplayToggle.hidden = false;
    this.updateAutoplayToggle();
  };

  SALCMotion.prototype.updateAutoplayToggle = function () {
    if (!this.autoplayToggle) return;

    var isPlaying = Boolean(this.timer);
    var label = isPlaying ? this.options.pauseLabel : this.options.playLabel;
    this.autoplayToggle.setAttribute('aria-label', label);

    if (this.generatedAutoplayToggle) this.autoplayToggle.textContent = label;
  };

  SALCMotion.prototype.pauseByUser = function () {
    if (!this.options.autoplay) return this;
    this.userPaused = true;
    this.stopAutoplay();
    this.updateAutoplayToggle();
    return this;
  };

  SALCMotion.prototype.play = function () {
    this.hasExplicitAutoplayChoice = true;
    this.userPaused = false;
    this.startAutoplay();
    return this;
  };

  SALCMotion.prototype.pause = function () {
    this.hasExplicitAutoplayChoice = true;
    return this.pauseByUser();
  };

  SALCMotion.prototype.toggleAutoplay = function () {
    if (this.timer) this.pause();
    else this.play();
    return this;
  };

  SALCMotion.prototype.startAutoplay = function () {
    if (
      this.isDestroyed ||
      !this.isEnabled ||
      !this.options.autoplay ||
      !this.canMove() ||
      this.userPaused ||
      this.hoverPaused ||
      this.dragPaused ||
      documentRef.hidden ||
      (globalScope.Shopify && globalScope.Shopify.designMode)
    ) {
      this.updateAutoplayToggle();
      return this;
    }

    this.stopAutoplay();
    var self = this;
    var delay = Math.max(this.options.autoplayDelay, this.getEffectiveSpeed() + 100);

    this.timer = globalScope.setInterval(function () {
      if (!self.effectiveLoop && !self.isPageLoop() && self.index >= self.getMaxIndex()) {
        self.pauseByUser();
        return;
      }
      self.next('autoplay');
    }, delay);

    this.track.setAttribute('aria-live', 'off');
    this.updateAutoplayToggle();
    return this;
  };

  SALCMotion.prototype.stopAutoplay = function () {
    if (this.timer) globalScope.clearInterval(this.timer);
    this.timer = null;
    if (this.track) this.track.setAttribute('aria-live', 'polite');
    this.updateAutoplayToggle();
    return this;
  };

  SALCMotion.prototype.onPointerDown = function (event) {
    if (!this.isEnabled || !this.options.draggable || !this.canMove()) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.isPrimary === false) return;
    if (event.target.closest && event.target.closest(ROOT_SELECTOR) !== this.root) return;
    if (event.target.closest && event.target.closest('button, input, select, textarea, label, summary')) return;

    if (this.isTransitioning) {
      this.cancelTransition();
      this.positionIndex = this.effectiveLoop
        ? this.index + this.cloneCount
        : this.getStopItemIndex(this.index);
      this.moveToPosition(this.positionIndex, false);
    }

    this.pointerId = event.pointerId;
    this.isPointerDown = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dragDelta = 0;
    this.dragMoved = false;
    this.dragAxisLocked = false;
    this.dragPaused = true;
    this.stopAutoplay();
    this.track.classList.remove('is-animating');
    this.root.classList.add('is-salc-motion-dragging');
    setPointerCaptureSafely(this.viewport, event.pointerId);
  };

  SALCMotion.prototype.onPointerMove = function (event) {
    if (!this.isEnabled || !this.isPointerDown || event.pointerId !== this.pointerId) return;

    var deltaX = event.clientX - this.startX;
    var deltaY = event.clientY - this.startY;

    if (!this.dragAxisLocked) {
      if (Math.abs(deltaX) <= 6 && Math.abs(deltaY) <= 6) return;

      this.dragAxisLocked = true;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.resetPointer(event.pointerId);
        this.startAutoplay();
        return;
      }
    }

    this.dragDelta = deltaX;
    this.dragMoved = true;
    if (event.cancelable) event.preventDefault();
    this.moveToPosition(this.positionIndex, false, this.dragDelta);
  };

  SALCMotion.prototype.onPointerUp = function (event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    var threshold = Math.max(35, this.viewport.getBoundingClientRect().width * 0.08);
    var delta = this.dragDelta;
    var moved = this.dragMoved;
    this.resetPointer(event.pointerId);

    if (moved) this.suppressClickUntil = Date.now() + 400;

    var wantsNext = this.isRTL ? delta > threshold : delta < -threshold;
    var wantsPrevious = this.isRTL ? delta < -threshold : delta > threshold;

    if (wantsNext) this.next('drag');
    else if (wantsPrevious) this.previous('drag');
    else this.moveToPosition(this.positionIndex, true);

    this.startAutoplay();
  };

  SALCMotion.prototype.onPointerCancel = function (event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;
    this.resetPointer(event.pointerId);
    this.moveToPosition(this.positionIndex, true);
    this.startAutoplay();
  };

  SALCMotion.prototype.resetPointer = function (pointerId) {
    this.pointerId = null;
    this.isPointerDown = false;
    this.dragAxisLocked = false;
    this.dragPaused = false;
    this.root.classList.remove('is-salc-motion-dragging');
    releasePointerCaptureSafely(this.viewport, pointerId);
  };

  SALCMotion.prototype.onClick = function (event) {
    if (Date.now() > this.suppressClickUntil) return;
    if (!event.target.closest || !event.target.closest('a, button')) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressClickUntil = 0;
  };

  SALCMotion.prototype.rememberItemState = function (item) {
    var existing = this.itemStates.find(function (state) { return state.item === item; });
    if (existing) return existing;

    var state = {
      item: item,
      attributes: captureAttributes(item, [
        'role',
        'aria-label',
        'aria-roledescription',
        'aria-current',
        'aria-hidden',
        'inert'
      ]),
      focusables: []
    };
    this.itemStates.push(state);
    return state;
  };

  SALCMotion.prototype.setItemVisibility = function (item, visible) {
    if (item.hasAttribute('data-salc-motion-clone')) return;

    var state = this.rememberItemState(item);
    item.classList.toggle('is-visible', visible);

    if (visible) {
      if (state.attributes['aria-hidden'] === null) item.removeAttribute('aria-hidden');
      else item.setAttribute('aria-hidden', state.attributes['aria-hidden']);
      if (state.attributes.inert === null) item.removeAttribute('inert');
      else item.setAttribute('inert', state.attributes.inert);
      state.focusables.forEach(function (focusState) {
        if (focusState.tabindex === null) focusState.element.removeAttribute('tabindex');
        else focusState.element.setAttribute('tabindex', focusState.tabindex);
      });
      state.focusables = [];
      return;
    }

    item.setAttribute('aria-hidden', 'true');
    item.setAttribute('inert', '');
    if (!state.focusables.length) {
      state.focusables = getFocusableElements(item).map(function (element) {
        return {
          element: element,
          tabindex: element.hasAttribute('tabindex') ? element.getAttribute('tabindex') : null
        };
      });
    }
    state.focusables.forEach(function (focusState) {
      focusState.element.setAttribute('tabindex', '-1');
    });
  };

  SALCMotion.prototype.restoreAllItemStates = function () {
    this.itemStates.forEach(function (state) {
      restoreAttributes(state.item, state.attributes);
      state.item.classList.remove('is-visible', 'is-selected', 'is-active');
      state.focusables.forEach(function (focusState) {
        if (focusState.tabindex === null) focusState.element.removeAttribute('tabindex');
        else focusState.element.setAttribute('tabindex', focusState.tabindex);
      });
    });
    this.itemStates = [];
  };

  SALCMotion.prototype.updateVisibleItems = function (position, dragOffset) {
    if (!this.viewport || !this.items.length) return;

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var translatedPosition = position + (this.isRTL ? dragOffset : -dragOffset);
    var selectedRealIndex = this.effectiveLoop ? this.index : this.getStopItemIndex(this.index);
    var onLoopClone = this.effectiveLoop &&
      (this.positionIndex < this.cloneCount || this.positionIndex >= this.cloneCount + this.realItems.length);

    this.realItems.forEach(function (item, realIndex) {
      var itemIndex = this.items.indexOf(item);
      var start = (this.positions[itemIndex] || 0) - translatedPosition;
      var end = start + (this.widths[itemIndex] || 0);
      var visible = end > 1 && start < viewportWidth - 1;

      if (onLoopClone && realIndex === selectedRealIndex) visible = true;
      this.setItemVisibility(item, visible);
    }, this);
  };

  SALCMotion.prototype.updateSelectedItems = function () {
    var selectedRealIndex = this.effectiveLoop ? this.index : this.getStopItemIndex(this.index);

    this.items.forEach(function (item, itemIndex) {
      var isVisualSelection = itemIndex === this.positionIndex;
      var isClone = item.hasAttribute('data-salc-motion-clone');
      var realIndex = isClone
        ? Number(item.getAttribute('data-salc-motion-clone-of'))
        : this.realItems.indexOf(item);

      item.classList.toggle('is-selected', isVisualSelection);
      item.classList.toggle('is-active', isVisualSelection);

      if (!isClone) item.setAttribute('aria-current', realIndex === selectedRealIndex ? 'true' : 'false');
      else item.removeAttribute('aria-current');
    }, this);
  };

  SALCMotion.prototype.updateDots = function () {
    this.dots.forEach(function (dot, index) {
      var active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    }, this);
  };

  SALCMotion.prototype.updateButtons = function () {
    var hideControls = !this.options.prevNextButtons || !this.canMove();

    [this.prevButton, this.nextButton].forEach(function (button) {
      if (!button) return;
      button.hidden = hideControls;
      if (!hideControls && (this.effectiveLoop || this.isPageLoop())) button.disabled = false;
    }, this);

    if (hideControls || this.effectiveLoop || this.isPageLoop()) return;
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= this.getMaxIndex();
  };

  SALCMotion.prototype.updateVisualState = function () {
    this.updateSelectedItems();
    this.updateDots();
    this.updateButtons();
    this.updateAutoplayToggle();
    this.root.classList.toggle('is-salc-motion-static', !this.canMove());
  };

  SALCMotion.prototype.applyDisabledState = function () {
    this.isEnabled = false;
    this.stopAutoplay();
    this.cancelTransition();
    this.removeClones();
    this.restoreAllItemStates();
    this.resetPointer(this.pointerId);

    this.items = this.realItems.slice();
    this.positions = [];
    this.widths = [];
    this.slideStops = [];
    this.track.style.transform = '';
    this.track.style.transitionDuration = '';
    this.track.style.paddingLeft = '';
    this.track.style.paddingRight = '';
    this.track.classList.remove('is-animating');
    this.root.classList.remove('is-salc-motion-ready', 'is-salc-motion-static', 'is-salc-motion-dragging');
    this.root.classList.add('is-salc-motion-disabled');

    if (this.prevButton) this.prevButton.hidden = true;
    if (this.nextButton) this.nextButton.hidden = true;
    if (this.dotsContainer) this.dotsContainer.hidden = true;
    if (this.autoplayToggle) this.autoplayToggle.hidden = true;
  };

  SALCMotion.prototype.disable = function () {
    this.isManuallyDisabled = true;
    this.applyDisabledState();
    dispatch(this.root, 'salc-motion:disable', { instance: this });
    return this;
  };

  SALCMotion.prototype.enable = function () {
    this.isManuallyDisabled = false;
    this.refresh(true);
    dispatch(this.root, 'salc-motion:enable', { instance: this, enabled: this.isEnabled });
    return this;
  };

  SALCMotion.prototype.destroy = function () {
    if (this.isDestroyed) return;

    this.isDestroyed = true;
    this.stopAutoplay();
    this.cancelTransition();
    this.resetPointer(this.pointerId);
    this.scheduleRefresh.cancel();

    this.unbinders.forEach(function (unbind) { unbind(); });
    this.unbinders = [];

    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
    this.resizeObserver = null;
    this.observedResizeTargets = [];
    this.mutationObserver = null;

    this.removeClones();
    this.restoreAllItemStates();

    if (this.track && this.originalTrackStyle) {
      this.track.style.transform = this.originalTrackStyle.transform;
      this.track.style.transitionDuration = this.originalTrackStyle.transitionDuration;
      this.track.style.paddingLeft = this.originalTrackStyle.paddingLeft;
      this.track.style.paddingRight = this.originalTrackStyle.paddingRight;
      this.track.style.direction = this.originalTrackStyle.direction;
      this.track.classList.remove('is-animating');
    }

    if (this.generatedAutoplayToggle && this.autoplayToggle && this.autoplayToggle.parentNode) {
      this.autoplayToggle.parentNode.removeChild(this.autoplayToggle);
    }

    restoreAttributes(this.root, this.originalRootAttributes);
    restoreAttributes(this.track, this.originalTrackAttributes);
    restoreAttributes(this.prevButton, this.originalPrevButtonAttributes);
    restoreAttributes(this.nextButton, this.originalNextButtonAttributes);
    restoreAttributes(this.dotsContainer, this.originalDotsAttributes);
    restoreAttributes(this.autoplayToggle, this.originalAutoplayToggleAttributes);
    if (this.dotsContainer) {
      toArray(this.dotsContainer.querySelectorAll('[data-salc-motion-generated-dot]')).forEach(function (dot) {
        if (dot.parentNode) dot.parentNode.removeChild(dot);
      });
    }
    this.dots = [];
    this.root.classList.remove(
      'is-salc-motion-ready',
      'is-salc-motion-static',
      'is-salc-motion-disabled',
      'is-salc-motion-dragging',
      'is-salc-motion-rtl'
    );
    this.root.removeAttribute('data-salc-motion-loop-fallback');
    this.root[INSTANCE_KEY] = null;
    unregister(this);

    dispatch(this.root, 'salc-motion:destroy', { instance: this });
  };

  SALCMotion.init = function (scope, options) {
    if (!documentRef) return [];

    var container = scope || documentRef;
    if (typeof container === 'string') container = documentRef.querySelector(container);
    if (!container) return [];

    var roots = container.matches && container.matches(ROOT_SELECTOR)
      ? [container]
      : toArray(container.querySelectorAll(ROOT_SELECTOR));

    return roots.map(function (root) {
      if (root[INSTANCE_KEY]) {
        if (options) root[INSTANCE_KEY].suppliedOptions = options;
        root[INSTANCE_KEY].refresh(true);
        return root[INSTANCE_KEY];
      }
      return new SALCMotion(root, options);
    });
  };

  SALCMotion.destroyAll = function (scope) {
    if (!documentRef) return;

    var container = scope || documentRef;
    if (typeof container === 'string') container = documentRef.querySelector(container);
    if (!container) return;

    INSTANCES.slice().forEach(function (instance) {
      if (instance.root === container || (container.contains && container.contains(instance.root))) {
        instance.destroy();
      }
    });
  };

  SALCMotion.getInstance = function (root) {
    var element = resolveElement(root);
    return element ? element[INSTANCE_KEY] || null : null;
  };

  SALCMotion.installJQueryPlugin = function (jQueryReference) {
    var jQuery = jQueryReference || (globalScope && globalScope.jQuery);
    if (!jQuery || !jQuery.fn || jQuery.fn.salcMotion) return false;

    jQuery.fn.salcMotion = function (command) {
      var args = Array.prototype.slice.call(arguments, 1);
      var returnValue = this;

      this.each(function () {
        var instance = this[INSTANCE_KEY];

        if (typeof command !== 'string') {
          if (!instance) instance = new SALCMotion(this, command || {});
          else {
            instance.suppliedOptions = command || instance.suppliedOptions;
            instance.refresh(true);
          }
          return;
        }

        if (command === 'instance') {
          returnValue = instance || null;
          return false;
        }

        if (!instance) instance = new SALCMotion(this);

        var methods = {
          next: 'next',
          previous: 'previous',
          goTo: 'goTo',
          play: 'play',
          pause: 'pause',
          refresh: 'refresh',
          enable: 'enable',
          disable: 'disable',
          destroy: 'destroy'
        };

        if (!methods[command]) throw new Error('Unknown SALC Motion command: ' + command);
        instance[methods[command]].apply(instance, args);
      });

      return returnValue;
    };

    return true;
  };

  SALCMotion.bindPlatformEvents = function () {
    if (!documentRef || SALCMotion.platformEventsBound) return;
    SALCMotion.platformEventsBound = true;

    documentRef.addEventListener('shopify:section:load', function (event) {
      SALCMotion.init(event.target);
    });

    documentRef.addEventListener('shopify:section:unload', function (event) {
      SALCMotion.destroyAll(event.target);
    });

    documentRef.addEventListener('shopify:block:select', function (event) {
      var root = event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
      var instance = root ? SALCMotion.getInstance(root) : null;
      var item = event.target.closest ? event.target.closest('[data-salc-motion-item]') : null;

      if (!instance || !item) return;
      var itemIndex = instance.getRealItems().indexOf(item);
      if (itemIndex < 0) return;

      instance.pause();
      instance.goTo(instance.effectiveLoop ? itemIndex : instance.findStopForItem(itemIndex), 'shopify-editor');
    });
  };

  SALCMotion.autoInit = function () {
    if (!documentRef) return;

    if (documentRef.readyState === 'loading') {
      documentRef.addEventListener('DOMContentLoaded', function () {
        SALCMotion.init(documentRef);
      }, { once: true });
    } else {
      SALCMotion.init(documentRef);
    }
  };

  SALCMotion.version = VERSION;
  SALCMotion.defaults = DEFAULTS;
  SALCMotion.platformEventsBound = false;

  return SALCMotion;
});
